import { useTranslation } from "react-i18next";
import { Vote, Plus, X, Check } from "lucide-react";
import { Card, CardContent, Button, Input } from "@tabipla/ui";
import type { ConfirmDialogProps } from "@tabipla/components";
import { ConfirmDialog } from "@tabipla/components";
import type { VotingPanelProps } from "./types";
import { PollCard } from "./PollCard";

interface VotingPanelPresenterProps extends VotingPanelProps {
  dialogProps: ConfirmDialogProps;
}

/**
 * VotingPanel presenter component.
 * Pure presentation - receives all data and callbacks as props.
 * Does not contain any hooks for data fetching or mutations.
 */
export function VotingPanel({
  polls,
  isLoading,
  isCreating,
  newQuestion,
  newOptions,
  allowMultiple,
  deadline,
  expandedPolls,
  isCreatingPoll,
  isVoting,
  isClosing,
  isDeleting,
  onNewQuestionChange,
  onOptionChange,
  onAddOption,
  onRemoveOption,
  onAllowMultipleChange,
  onDeadlineChange,
  onCreate,
  onCancelCreate,
  onStartCreate,
  onVote,
  onClose,
  onDelete,
  onToggleExpand,
  dialogProps,
}: VotingPanelPresenterProps) {
  const { t } = useTranslation();

  // Helper function to calculate poll card props
  const getPollCardProps = (poll: (typeof polls)[0]) => {
    const maxVotes = Math.max(...poll.results.map((r) => r.votes), 1);
    const totalOptionVotes = poll.results.reduce((sum, r) => sum + r.votes, 0);
    const winnerIndex =
      totalOptionVotes > 0
        ? poll.results.reduce((winnerIdx, result, idx, arr) => {
            if (result.votes > arr[winnerIdx].votes) return idx;
            if (result.votes === arr[winnerIdx].votes && idx !== winnerIdx) return -1;
            return winnerIdx;
          }, 0 as number)
        : -1;
    const isDisabled = poll.isClosed || poll.isExpired;

    return { maxVotes, totalOptionVotes, winnerIndex, isDisabled };
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-center py-8">
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header with Create Button */}
      <Card className="overflow-hidden">
        <CardContent className="p-4">
          {!isCreating ? (
            <Button
              onClick={onStartCreate}
              className="w-full rounded-xl py-6 text-base font-medium shadow-sm transition-all hover:shadow-md active:scale-[0.98]"
            >
              <Plus className="mr-2 h-5 w-5" />
              {t("polls.createNew")}
            </Button>
          ) : (
            <form onSubmit={onCreate} className="space-y-4">
              <div>
                <Input
                  value={newQuestion}
                  onChange={(e) => onNewQuestionChange(e.target.value)}
                  placeholder={t("polls.questionPlaceholder")}
                  className="font-medium"
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium">{t("polls.options")}</label>
                {newOptions.map((option, index) => (
                  <div key={index} className="flex gap-2">
                    <Input
                      value={option}
                      onChange={(e) => onOptionChange(index, e.target.value)}
                      placeholder={t("polls.optionPlaceholder", { number: index + 1 })}
                    />
                    {newOptions.length > 2 && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => onRemoveOption(index)}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                ))}
                {newOptions.length < 10 && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={onAddOption}
                    className="w-full"
                  >
                    <Plus className="mr-2 h-4 w-4" />
                    {t("polls.addOption")}
                  </Button>
                )}
              </div>

              <label className="flex cursor-pointer items-center gap-3 rounded-xl border p-3 transition-colors hover:bg-muted/50 active:bg-muted">
                <div
                  className={`flex h-5 w-5 items-center justify-center rounded-md border-2 transition-all ${
                    allowMultiple
                      ? "border-primary bg-primary text-primary-foreground"
                      : "border-muted-foreground/30"
                  }`}
                  onClick={() => onAllowMultipleChange(!allowMultiple)}
                >
                  {allowMultiple && <Check className="h-3 w-3" />}
                </div>
                <span className="text-sm" onClick={() => onAllowMultipleChange(!allowMultiple)}>
                  {t("polls.allowMultiple")}
                </span>
              </label>

              <div className="space-y-2">
                <label className="text-sm font-medium">
                  {t("polls.deadline")}{" "}
                  <span className="text-muted-foreground">({t("common.optional")})</span>
                </label>
                <Input
                  type="datetime-local"
                  value={deadline}
                  onChange={(e) => onDeadlineChange(e.target.value)}
                  min={new Date().toISOString().slice(0, 16)}
                />
              </div>

              <div className="flex gap-2">
                <Button type="button" variant="outline" onClick={onCancelCreate} className="flex-1">
                  {t("common.cancel")}
                </Button>
                <Button
                  type="submit"
                  disabled={
                    !newQuestion.trim() ||
                    newOptions.filter((o) => o.trim()).length < 2 ||
                    isCreatingPoll
                  }
                  className="flex-1"
                >
                  {t("polls.create")}
                </Button>
              </div>
            </form>
          )}
        </CardContent>
      </Card>

      {/* Polls List */}
      {polls.length === 0 ? (
        <Card className="overflow-hidden">
          <CardContent className="py-12 text-center">
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-muted">
              <Vote className="h-8 w-8 text-muted-foreground" />
            </div>
            <p className="font-medium text-muted-foreground">{t("polls.noPolls")}</p>
            <p className="mt-1 text-sm text-muted-foreground">{t("polls.noPollsDescription")}</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {polls.map((poll) => {
            const { maxVotes, totalOptionVotes, winnerIndex, isDisabled } = getPollCardProps(poll);
            return (
              <PollCard
                key={poll.id}
                poll={poll}
                isExpanded={expandedPolls.has(poll.id)}
                maxVotes={maxVotes}
                totalOptionVotes={totalOptionVotes}
                winnerIndex={winnerIndex}
                isDisabled={isDisabled}
                isVoting={isVoting}
                isClosing={isClosing}
                isDeleting={isDeleting}
                onToggleExpand={() => onToggleExpand(poll.id)}
                onVote={(optionIndex) => onVote(poll.id, optionIndex)}
                onClose={() => onClose(poll.id)}
                onDelete={() => onDelete(poll.id)}
              />
            );
          })}
        </div>
      )}

      <ConfirmDialog {...dialogProps} />
    </div>
  );
}
